/* eslint-disable react/no-unescaped-entities */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import {
  Clock, ClipboardCheck, UserCog, Shield, Crown, HardHat, Printer, ShieldAlert,
  Edit2, Plus, Lock, Send, CalendarClock, HelpCircle, ArrowRight, CheckCircle2,
} from 'lucide-react';
import { RESPONSIBILITIES, STATUS_CHAIN, buildTimekeepingHandoutHtml } from './timekeepingHandout';
import { resolveTimeZone, describeTzSpec, getTimezoneAbbreviation } from '../../utils/timezone';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Card = ({ children, className = '', ...rest }) => (
  <div className={`bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-800 rounded-xl shadow-lg overflow-hidden ${className}`} {...rest}>{children}</div>
);
const CardHeader = ({ children, className = '' }) => (
  <div className={`px-6 py-4 border-b border-gray-200 dark:border-slate-800 ${className}`}>{children}</div>
);
const CardTitle = ({ children }) => (
  <h2 className="text-xl font-bold text-gray-900 dark:text-slate-100 flex items-center gap-2">{children}</h2>
);
const CardContent = ({ children }) => <div className="p-6 space-y-5">{children}</div>;
const Section = ({ title, icon: Icon, children }) => (
  <section className="border-t border-gray-200 dark:border-slate-800 pt-5 first:border-0 first:pt-0">
    <h3 className="text-lg font-bold text-gray-900 dark:text-slate-100 mb-2 flex items-center gap-2">
      {Icon && <Icon className="w-5 h-5 text-blue-600 dark:text-blue-400" />}{title}
    </h3>
    <div className="space-y-3 text-sm text-gray-700 dark:text-slate-300 leading-relaxed">{children}</div>
  </section>
);
const Steps = ({ items }) => (
  <ol className="space-y-2 list-decimal list-outside pl-5 text-gray-700 dark:text-slate-300">
    {items.map((it, i) => <li key={i} className="leading-relaxed">{it}</li>)}
  </ol>
);
const Callout = ({ tone = 'blue', title, children }) => {
  const tones = {
    blue: 'bg-blue-50 dark:bg-blue-950/30 border-blue-200 dark:border-blue-800 text-blue-900 dark:text-blue-100',
    amber: 'bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-800 text-amber-900 dark:text-amber-100',
    green: 'bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-800 text-emerald-900 dark:text-emerald-100',
  };
  return (
    <div className={`rounded-lg border p-3 text-sm leading-relaxed ${tones[tone]}`}>
      {title && <p className="font-semibold mb-0.5">{title}</p>}
      <div>{children}</div>
    </div>
  );
};

const ROLE_META = {
  worker: { icon: HardHat, color: 'text-blue-600 dark:text-blue-400', bg: 'bg-blue-50 dark:bg-blue-950/40' },
  squad: { icon: Crown, color: 'text-amber-600 dark:text-amber-400', bg: 'bg-amber-50 dark:bg-amber-950/40' },
  supervisor: { icon: UserCog, color: 'text-violet-600 dark:text-violet-400', bg: 'bg-violet-50 dark:bg-violet-950/40' },
  admin: { icon: Shield, color: 'text-rose-600 dark:text-rose-400', bg: 'bg-rose-50 dark:bg-rose-950/40' },
};

/** Compact "your responsibilities" checklist — dropped at the top of each role tab. */
export const ResponsibilitiesChecklist = ({ role, onOpenGuide }) => {
  const r = RESPONSIBILITIES[role];
  const m = ROLE_META[role];
  if (!r) return null;
  return (
    <Card className="mb-6" data-testid={`responsibilities-checklist-${role}`}>
      <CardHeader className={m.bg}>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <CardTitle><ClipboardCheck className={`w-6 h-6 ${m.color}`} /> Your timekeeping responsibilities</CardTitle>
          <Button size="sm" variant="outline" onClick={onOpenGuide} data-testid={`responsibilities-open-guide-${role}`}>
            Full timekeeping & payroll guide <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
          </Button>
        </div>
      </CardHeader>
      <div className="p-6">
        <ul className="space-y-2">
          {r.items.map((it, i) => (
            <li key={i} className="flex gap-2.5 text-sm text-gray-700 dark:text-slate-300 leading-relaxed">
              <CheckCircle2 className={`w-4 h-4 mt-0.5 flex-shrink-0 ${m.color}`} />
              <span>{it}</span>
            </li>
          ))}
        </ul>
      </div>
    </Card>
  );
};

const addDays = (ymd, n) => {
  const d = new Date(`${ymd}T12:00:00`);
  d.setDate(d.getDate() + n);
  return d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
};

const CYCLE_LABEL = { weekly: 'Weekly', biweekly: 'Every two weeks', semimonthly: 'Twice a month (1st–15th, 16th–end)', monthly: 'Monthly' };

const fmtDeadline = (iso, tz) => {
  const d = new Date(iso);
  const zone = resolveTimeZone(tz, d);
  return `${d.toLocaleString(undefined, { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', ...(zone ? { timeZone: zone } : {}) })}${tz ? ` ${getTimezoneAbbreviation(tz, d)}` : ''}`;
};

const ExampleTimeline = ({ payPeriod, cutoffs }) => {
  const end = payPeriod?.current?.end;
  const dl = cutoffs?.current?.deadlines;
  const c = cutoffs?.cutoffs;
  const rows = [
    { key: 'certify', day: c?.certify_days ?? 0, who: 'Workers', what: 'Certify every shift in My Time (reminder goes out 24 h before)' },
    { key: 'approve', day: c?.approve_days ?? 1, who: 'Supervisors', what: 'Approve or return all certified shifts; certify-for kiosk crews; clear team exceptions' },
    { key: 'lock', day: c?.lock_days ?? 2, who: 'Administrators', what: 'Payroll-approve, override anything still uncertified (reason), fix/add time in the Workbench' },
    { key: 'lock', day: c?.lock_days ?? 2, who: 'Administrators', what: 'Lock the pay period, push to QuickBooks or download the payroll CSV, mark exported' },
  ];
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm" data-testid="payroll-example-timeline">
        <thead>
          <tr className="text-left text-xs uppercase tracking-wide text-gray-500 dark:text-slate-500">
            <th className="py-2 pr-3">When</th><th className="py-2 pr-3">Who</th><th className="py-2">What</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-t border-gray-200 dark:border-slate-800">
              <td className="py-2 pr-3 whitespace-nowrap font-semibold text-gray-900 dark:text-slate-100">
                {dl?.[r.key] ? `${fmtDeadline(dl[r.key], cutoffs?.timezone)} (${describeTzSpec(cutoffs?.timezone)})` : end ? addDays(end, r.day) : `Period end ${r.day ? `+ ${r.day} day${r.day > 1 ? 's' : ''}` : ''}`}
              </td>
              <td className="py-2 pr-3 whitespace-nowrap text-gray-700 dark:text-slate-300">{r.who}</td>
              <td className="py-2 text-gray-700 dark:text-slate-300">{r.what}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default function TimekeepingGuideTab() {
  const { token, company } = useAuth();
  const [payPeriod, setPayPeriod] = useState(null);
  const [cutoffs, setCutoffs] = useState(null);

  useEffect(() => {
    if (!token) return;
    const h = { headers: { Authorization: `Bearer ${token}` } };
    axios.get(`${API}/company/pay-period`, h).then((r) => setPayPeriod(r.data)).catch(() => setPayPeriod(null));
    axios.get(`${API}/company/payroll-cutoffs`, h).then((r) => setCutoffs(r.data)).catch(() => setCutoffs(null));
  }, [token]);

  const printHandout = () => {
    const w = window.open('', '_blank');
    if (!w) return;
    w.document.write(buildTimekeepingHandoutHtml(company?.name));
    w.document.close();
    setTimeout(() => { w.focus(); w.print(); }, 350);
  };

  return (
    <div className="space-y-6" data-testid="timekeeping-guide-tab">
      <div className="rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 text-white p-5 shadow-md">
        <div className="flex items-start gap-3">
          <Clock className="w-7 h-7 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-xs uppercase tracking-widest opacity-80">Guide</p>
            <h2 className="text-2xl font-extrabold">Timekeeping → Payroll: who does what</h2>
            <p className="text-sm opacity-95 mt-1 max-w-3xl">
              One shift goes through six hands-offs before it becomes a pay cheque. This page spells out each role's job,
              the cut-offs, and exactly how administrators approve, fix, or override time so nobody is paid late.
            </p>
          </div>
          <Button size="sm" variant="secondary" onClick={printHandout} data-testid="print-handout-btn">
            <Printer className="w-3.5 h-3.5 mr-1.5" /> Print one-page handout
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader className="bg-emerald-50 dark:bg-emerald-950/40">
          <CardTitle><ArrowRight className="w-6 h-6 text-emerald-600 dark:text-emerald-400" /> 1. The journey of a shift</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-center gap-2" data-testid="status-chain">
            {STATUS_CHAIN.map((s, i) => (
              <React.Fragment key={s.key}>
                <div className="rounded-lg border border-gray-200 dark:border-slate-700 px-3 py-2 min-w-[150px]">
                  <p className="text-sm font-bold text-gray-900 dark:text-slate-100">{s.label}</p>
                  <p className="text-[11px] uppercase tracking-wide text-blue-600 dark:text-blue-400">{s.who}</p>
                  <p className="text-xs text-gray-600 dark:text-slate-400 mt-1">{s.hint}</p>
                </div>
                {i < STATUS_CHAIN.length - 1 && <ArrowRight className="w-4 h-4 text-gray-400 hidden sm:block" />}
              </React.Fragment>
            ))}
          </div>
          <Callout tone="blue" title="Is worker certification mandatory?">
            It is strongly recommended — it is the worker's signature on their own hours — but it is not a hard block.
            Administrators can approve uncertified or unreviewed time with a documented override (section 4), and can lock a
            period below 100% readiness with a reason. Every override is audit-logged and the worker is notified.
          </Callout>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="bg-blue-50 dark:bg-blue-950/40">
          <CardTitle><ClipboardCheck className="w-6 h-6 text-blue-600 dark:text-blue-400" /> 2. Responsibilities by role</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {Object.entries(RESPONSIBILITIES).map(([key, r]) => {
              const m = ROLE_META[key];
              return (
                <div key={key} className={`rounded-xl border border-gray-200 dark:border-slate-800 p-4 ${m.bg}`} data-testid={`role-responsibilities-${key}`}>
                  <p className="font-bold text-gray-900 dark:text-slate-100 flex items-center gap-2 mb-2"><m.icon className={`w-5 h-5 ${m.color}`} /> {r.title}</p>
                  <ul className="space-y-1.5">
                    {r.items.map((it, i) => (
                      <li key={i} className="flex gap-2 text-sm text-gray-700 dark:text-slate-300 leading-relaxed">
                        <CheckCircle2 className={`w-4 h-4 mt-0.5 flex-shrink-0 ${m.color}`} /><span>{it}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="bg-teal-50 dark:bg-teal-950/40">
          <CardTitle><CalendarClock className="w-6 h-6 text-teal-600 dark:text-teal-400" /> 3. Example cut-off timeline</CardTitle>
        </CardHeader>
        <CardContent>
          {payPeriod?.configured ? (
            <p data-testid="pay-period-summary">
              Your company pays <strong>{CYCLE_LABEL[payPeriod.config?.cycle] || payPeriod.config?.cycle}</strong>. The current period runs{' '}
              <strong>{payPeriod.current.start}</strong> → <strong>{payPeriod.current.end}</strong>. A suggested rhythm for this period:
            </p>
          ) : (
            <p data-testid="pay-period-summary">
              {token ? 'No pay-period cycle is configured yet (Reports → Payroll → Pay Period). Once set, this table shows real dates.' : 'Sign in to see this table with your company\u2019s real pay-period dates.'} A suggested rhythm:
            </p>
          )}
          <ExampleTimeline payPeriod={payPeriod} cutoffs={cutoffs} />
          {cutoffs?.configured ? (
            <Callout tone="green" title="These are your company's cut-offs">
              Set in Settings → Company → Payroll Cut-offs. Workers are reminded (bell + email) 24 hours before the certify deadline
              with the list of shifts they still need to certify. Change the deadlines any time — this table and the reminders follow.
            </Callout>
          ) : (
            <Callout tone="amber" title="These are suggested defaults">
              Make them real in <strong>Settings → Company → Payroll Cut-offs</strong> (days after period end + time). Once saved, this
              table shows your deadlines, and workers get an automatic reminder 24 hours before the certify cut-off.
            </Callout>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="bg-rose-50 dark:bg-rose-950/40">
          <CardTitle><Shield className="w-6 h-6 text-rose-600 dark:text-rose-400" /> 4. Administrators — step by step to a clean pay run</CardTitle>
        </CardHeader>
        <CardContent>
          <Section title="A. Check readiness" icon={ClipboardCheck}>
            <Steps items={[
              <>Sidebar → <strong>Money → Payroll Review</strong>. Pick <strong>Current</strong> or <strong>Previous</strong> period (or a custom range).</>,
              <>The readiness card shows the % of shifts that are payroll-approved or better, plus what is <strong>blocking</strong>: <em>Not submitted by worker</em>, <em>Awaiting supervisor approval</em>, <em>Returned to worker</em>, <em>Flagged by payroll</em>, <em>Awaiting payroll approval</em>.</>,
              'Click any blocking pill to open the drill-down list of the exact shifts and workers.',
            ]} />
          </Section>

          <Section title="B. Approve time the normal way" icon={CheckCircle2}>
            <Steps items={[
              <>Scroll to the <strong>Payroll queue</strong> — every shift a supervisor has approved for this period.</>,
              <>Open a shift to see punches, breaks, allocations and exceptions. Click <strong>Approve</strong> (optionally set a pay code / shift premium), or <strong>Approve all</strong> once you have skimmed the list.</>,
              <>Doubt something? <strong>Flag exception</strong> (reason required — it leaves the queue until you resolve it) or <strong>Send back</strong> to the supervisor with a reason.</>,
            ]} />
          </Section>

          <Section title="C. Worker has not reviewed their time after the cut-off — override" icon={ShieldAlert}>
            <p>New staff often miss the certification step. Do not hold the whole payroll for it:</p>
            <Steps items={[
              <>In Payroll Review click the <strong>Not submitted by worker</strong> pill (or <strong>Awaiting supervisor approval</strong> if the supervisor is away).</>,
              <>Confirm the hours look right (times, job, breaks). If they don't, fix them first (step D).</>,
              <>Click <strong>Approve (override)</strong> on a single shift, or <strong>Approve all shown (override)</strong> for the whole list.</>,
              <>Type a reason of at least 10 characters — e.g. "Did not certify by Monday cut-off; hours confirmed with supervisor". This goes into the audit log.</>,
              <>The shift jumps straight to <strong>Payroll approved</strong>. The worker gets an in-app notice explaining what happened and to report anything that looks wrong.</>,
              'Follow up: show the worker My Time → Needs review → Certify so it doesn\u2019t repeat next period.',
            ]} />
            <Callout tone="amber">
              Override approval skips the worker's signature and the supervisor's review. Use it to keep people paid on time — not
              as the default path. The audit log records who overrode what, when and why.
            </Callout>
          </Section>

          <Section title="D. Fix a wrong punch (edit time)" icon={Edit2}>
            <Steps items={[
              <>Sidebar → <strong>Reports → Payroll</strong> tab → <strong>Payroll Workbench</strong>. Search the worker and expand their row.</>,
              <>Click the session → <strong>Edit</strong>. Change clock-in / clock-out (and job / project if needed), pick a reason class and add a note.</>,
              'Save. The original punch is preserved, the entry is marked Edited, the hours difference shows in the period stats, and the worker receives an edit notice.',
              <>Shift still open (worker forgot to clock out)? Edit the session and set the real clock-out time — an open shift cannot be approved or pushed.</>,
            ]} />
          </Section>

          <Section title="E. Add hours that are missing entirely" icon={Plus}>
            <Steps items={[
              <>Payroll Workbench → worker row → <strong>Add session</strong>.</>,
              'Enter the date, start time and hours, optional job / project, a reason class and a note (e.g. "Kiosk offline, verified with supervisor").',
              <>Save. The new shift is created as <strong>Payroll approved</strong> with an admin-override marker — it does not need worker certification and will not block the lock.</>,
            ]} />
          </Section>

          <Section title="F. Lock the period" icon={Lock}>
            <Steps items={[
              <>Payroll Review bottom bar → <strong>Lock Pay Period</strong>. At 100% readiness it locks immediately.</>,
              <>Below 100% the button asks for an <strong>override reason</strong>; approved shifts are locked, anything still unreviewed is skipped and listed — go back to step C for those, then lock again.</>,
              <>Optionally run the <strong>Pre-lock checklist</strong> in the Payroll Workbench first (open sessions, very long shifts, edits).</>,
              'Locked time cannot be edited by anyone. Post-lock corrections go through Payroll Review → Payroll Adjustments (request → approve → apply).',
            ]} />
          </Section>

          <Section title="G. Pay" icon={Send}>
            <Steps items={[
              <>Push to <strong>QuickBooks</strong> from the Payroll Workbench (preview first). Only payroll-approved / locked hours pass the approval gate — held-back days show as "Not approved".</>,
              <>No QuickBooks? Download the <strong>payroll CSV</strong> (Regular / OT / callback / approved columns) for your payroll provider.</>,
              <>Payroll Review bottom bar → <strong>Mark exported</strong> with a batch reference → shifts become <strong>Paid</strong>.</>,
            ]} />
          </Section>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="bg-violet-50 dark:bg-violet-950/40">
          <CardTitle><HelpCircle className="w-6 h-6 text-violet-600 dark:text-violet-400" /> 5. Common situations</CardTitle>
        </CardHeader>
        <CardContent>
          <Section title="“I clocked in but there is nothing to certify”">
            <p>Only <em>closed</em> shifts (clocked out) appear under Needs review. Clock out first; shifts under one minute are hidden.</p>
          </Section>
          <Section title="“My hours are wrong — should I certify?”">
            <p>No. Tap <strong>Request a correction</strong> and describe the fix; your supervisor approves it into a real entry, then you certify the corrected shift.</p>
          </Section>
          <Section title="Supervisor is on vacation">
            <p>Admins can approve directly from Payroll Review using <strong>Approve (override)</strong> on the "Awaiting supervisor approval" list, with a reason.</p>
          </Section>
          <Section title="Kiosk crew without phones">
            <p>Kiosk punches are real time entries. Workers can certify from any browser (My Time). If they can't, the supervisor opens <strong>Pending Approvals → Not Certified</strong> and taps <strong>Certify for worker</strong> with a reason — the shift is certified and approved in one step, audit-logged, and the worker is notified.</p>
          </Section>
          <Section title="Who keeps skipping certification?">
            <p><strong>Payroll Review → Overrides</strong> lists every shift approved outside the normal chain this period (admin overrides, admin-added sessions, supervisor certify-for) grouped by worker, with a <strong>REPEAT</strong> flag at 3+ shifts or over half their shifts — the coaching list for your next toolbox talk.</p>
          </Section>
          <Section title="Paid vs unpaid breaks">
            <p>Set the company default in Settings → Meal Breaks and override per person in Staff → Edit. Unpaid-break staff confirm their break at clock-out (30 min deducted with an audit note); paid-break staff are never prompted.</p>
          </Section>
          <Callout tone="green" title="Everything is traceable">
            Every certify, approve, return, edit, override, lock and export writes to the time audit log (Admin → Audit Log, or the
            per-shift audit trail in Payroll Review). Nothing is rounded automatically.
          </Callout>
        </CardContent>
      </Card>
    </div>
  );
}
